import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {UiStateService} from '@llm/service/signals_ui_state.service';
import {llmStoreService} from "@llm/service/llm_store.service";
import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service";

/**
 * Interface para as configurações de fala opcionais.
 */
export interface SpeechSettings {
  language?: string;
  rate?: number;
  pitch?: number;
  volume?: number;
  voiceName?: string;
}

/**
 * Serviço responsável por toda a lógica de síntese de voz (TTS - Text-to-Speech).
 * Gerencia a reprodução, pausa, retomada e cancelamento da fala,
 * além de expor o estado de fala atual.
 */
@Injectable({
  providedIn: 'root'
})
export class SpeechSynthesizerService {
  //Injectables
  private store = inject(llmStoreService)
  private _console = inject(Tools_aiConsoleService);

  private synth: SpeechSynthesis;
  private voices: SpeechSynthesisVoice[] = [];
  private voicesLoaded: Promise<void>;

  // Constantes para configurações padrão
  private static readonly DEFAULT_VOICE_NAME = "Microsoft Antonio Online (Natural) - Portuguese (Brazil)";
  private static readonly DEFAULT_LANGUAGE = "pt-BR";
  private static readonly DEFAULT_RATE = 1.1;
  private static readonly DEFAULT_PITCH = 1;
  private static readonly DEFAULT_VOLUME = 1;

  // Subject para notificar em tempo real se o serviço está a falar
  //private isSpeakingSubject = new BehaviorSubject<boolean>(false);
  /**
   * Observable que emite `true` se o serviço de síntese de voz estiver a falar,
   * e `false` caso contrário.
   */
  //public isSpeaking$: Observable<boolean> = this.isSpeakingSubject.asObservable();

  constructor(
    private _uiStateService: UiStateService,
  ) {
    this.synth = window.speechSynthesis;
    // Promessa para garantir que as vozes estejam carregadas antes de tentar falar
    this.voicesLoaded = new Promise(resolve => {
      if (this.synth.onvoiceschanged !== undefined) {
        this.synth.onvoiceschanged = () => {
          this.loadVoices();
          resolve();
        };
      }
      this.loadVoices(); // Tentar carregar as vozes imediatamente
      if (this.voices.length > 0) {
        resolve(); // Se já houver vozes, resolve imediatamente
      }
      // Fallback para carregar vozes se onvoiceschanged não disparar ou for lento
      setTimeout(() => {
        if (this.voices.length === 0) {
          this.loadVoices();
          resolve();
        }
      }, 1000);
    });
  }

  /**
   * Carrega as vozes disponíveis no sistema.
   * É chamado quando o evento `onvoiceschanged` é disparado ou no construtor.
   */
  private loadVoices(): void {
    this.voices = this.synth.getVoices();
    //console.log('SpeechSynthesizerService: Vozes carregadas.', this.voices);
  }

  /**
   * Imprime na consola todas as vozes disponíveis, mostrando o seu nome e idioma.
   */
  public printAvailableVoices(): void {
    if (this.voices.length === 0) {
      this._console.warn("SpeechSynthesizerService: Nenhuma voz de síntese de fala disponível.");
      return;
    }
    this._console.log("SpeechSynthesizerService: Vozes disponíveis:");
    this.voices.forEach(voice => {
      this._console.log(`- Nome: ${voice.name}, Idioma: ${voice.lang}, Padrão: ${voice.default}`);
    });
  }

  /**
   * Sintetiza o texto fornecido, utilizando a configuração opcional.
   * Por padrão, usa o idioma português do Brasil ("pt-BR").
   *
   * @param text Texto que será reproduzido.
   * @param settings Configuração de síntese: idioma, velocidade, tom, volume e/ou voz.
   * @returns Uma Promise que resolve quando a fala termina, ou rejeita se houver um erro.
   */
  public async speak(text: string, settings?: SpeechSettings): Promise<void> {
    return new Promise(async (resolve, reject) => {
      if (!this.synth) {
        this._console.error("SpeechSynthesizerService: A síntese de voz não é suportada neste navegador.");
           this._uiStateService.stopAudioPlayback();
        this.store.toggleSpeaking();
        //this.isSpeakingSubject.next(false);
        return reject("A síntese de voz não é suportada.");
      }

      await this.voicesLoaded; // Aguarda o carregamento das vozes

      // Se já estiver a falar, cancelar a fala atual para começar uma nova.
      // Isto evita enfileirar falas, o que pode atrasar a atualização do estado.
      if (this.synth.speaking) {
        this.synth.cancel();
      }

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = settings?.language || SpeechSynthesizerService.DEFAULT_LANGUAGE;
      utterance.rate = settings?.rate || SpeechSynthesizerService.DEFAULT_RATE;
      utterance.pitch = settings?.pitch || SpeechSynthesizerService.DEFAULT_PITCH;
      utterance.volume = settings?.volume || SpeechSynthesizerService.DEFAULT_VOLUME;

      let selectedVoice: SpeechSynthesisVoice | undefined;
      const desiredVoiceName = settings?.voiceName || SpeechSynthesizerService.DEFAULT_VOICE_NAME;

      // Tenta encontrar a voz desejada pelo nome
      if (desiredVoiceName) {
        selectedVoice = this.voices.find(voice => voice.name === desiredVoiceName);
      }

      // Se a voz desejada não for encontrada, tenta encontrar uma voz pelo idioma
      if (!selectedVoice) {
        selectedVoice = this.voices.find(voice => voice.lang === utterance.lang);
        if (selectedVoice) {
          this._console.warn(`SpeechSynthesizerService: Voz "${desiredVoiceName}" não encontrada. Usando a voz "${selectedVoice.name}" (${selectedVoice.lang}).`);
        } else {
          // Fallback para a voz padrão ou a primeira disponível
          selectedVoice = this.voices.find(voice => voice.name === SpeechSynthesizerService.DEFAULT_VOICE_NAME) || this.voices[0];
          if (selectedVoice) {
            this._console.warn(`SpeechSynthesizerService: Nenhuma voz para ${utterance.lang} ou "${desiredVoiceName}" encontrada. Usando a voz padrão "${selectedVoice.name}" (${selectedVoice.lang}).`);
          } else {
            this._console.error("SpeechSynthesizerService: Nenhuma voz de síntese de fala disponível no sistema.");
               this._uiStateService.stopAudioPlayback();
        this.store.toggleSpeaking();
            //this.isSpeakingSubject.next(false);
            return reject("Nenhuma voz de síntese de fala disponível.");
          }
        }
      }

      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
      this._console.log('####Voz seleccionada es###:', utterance.voice)
      // Define os callbacks para atualizar o estado e resolver/rejeitar a Promise
      utterance.onstart = () => {
        //this.isSpeakingSubject.next(true);
        this._uiStateService.startAudioPlayback();
        this.store.toggleSpeaking();
        this._console.log('SpeechSynthesizerService: Fala iniciada.');
      };
      utterance.onend = () => {
        //this.isSpeakingSubject.next(false);
           this._uiStateService.stopAudioPlayback();
        this.store.toggleSpeaking();
        this._console.log('SpeechSynthesizerService: Fala terminada.');
        resolve();
      };
      utterance.onerror = (event) => {
        this._console.error("SpeechSynthesizerService: Erro na síntese de voz:", event);
           this._uiStateService.stopAudioPlayback();
        this.store.toggleSpeaking();
        //this.isSpeakingSubject.next(false);
        reject(event.error);
      };
         this._uiStateService.startAudioPlayback();
        this.store.toggleSpeaking();
      this._console.log('####Voz seleccionada es###:', utterance.voice)
      this.synth.speak(utterance);
    });
  }

  /**
   * Pausa a síntese se estiver a reproduzir.
   */
  public pause(): void {
    if (this.synth.speaking && !this.synth.paused) {
      this.synth.pause();
         this._uiStateService.stopAudioPlayback();
        this.store.toggleSpeaking();
      //this.isSpeakingSubject.next(false); // Considera pausado como não reproduzindo ativamente
      this._console.log('SpeechSynthesizerService: Fala pausada.');
    }
  }

  /**
   * Retoma a síntese se estiver em pausa.
   */
  public resume(): void {
    if (this.synth.paused) {
      this.synth.resume();
         this._uiStateService.startAudioPlayback();
        this.store.toggleSpeaking();
      //this.isSpeakingSubject.next(true);
      this._console.log('SpeechSynthesizerService: Fala retomada.');
    }
  }

  /**
   * Cancela a síntese em curso.
   */
  public cancel(): void {
    if (this.synth.speaking || this.synth.paused) {
      this.synth.cancel();
         this._uiStateService.stopAudioPlayback();
        this.store.toggleSpeaking();
      // this.isSpeakingSubject.next(false);
      this._console.log('SpeechSynthesizerService: Fala cancelada.');
    }
  }

  /**
   * Verifica de forma síncrona se o serviço de voz está a falar.
   * Útil para verificações rápidas em lógica de componente.
   */
  // public _isCurrentlySpeaking(): boolean {
  //   return this.isSpeakingSubject.value;
  // }
}
